import React, { useEffect, useMemo, useState } from 'react';
import { Fieldset } from 'primereact/fieldset';
import { InputText } from 'primereact/inputtext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { Toolbar } from 'primereact/toolbar';
import InstrumentService from './instrument-service';
import { useToast } from '../../../components/Toast';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import RequiredLabel from '../../../components/RequiredLabel';
import { QRCodeCanvas } from 'qrcode.react';
import { Dropdown } from 'primereact/dropdown';
import ASTInstrumentService from '../../../services/ast/ASTInstrumentService';
import { useDialog } from '../../../components/Dialog';
import instrumentService from './instrument-service';
import { InputSwitch } from 'primereact/inputswitch';


const InstrumentEdit: React.FC = () => {

  const [clientState, setClientState] = useState<any>(null);
  const navigate = useNavigate();
  const { showConfirmDialog } = useDialog();
  const { showToast } = useToast();
  const location = useLocation();
  const selectedItem = location.state?.selectedItem;
  const isEditMode = !!selectedItem;
  const [entity, setEntity] = useState<any>(null);  // Track selected entity
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState<any>({
    insertDate: new Date(),
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const requiredFields = ["snNo", "itemCode", "entity"];
  // const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  const [qrData, setQrData] = useState<any>(null);

  const [entityDropdown, setEntityDropdown] = useState<any[]>([]);
  const entityDropdownOptions = useMemo(() => entityDropdown, [entityDropdown]);

  const [equipmentTypeDropdown, setEquipmentTypeDropdown] = useState<any[]>([]);
  const equipmentTypeDropdownOptions = useMemo(() => equipmentTypeDropdown, [equipmentTypeDropdown]);

  const [stateDropdown, setStateDropdown] = useState<any[]>([]);
  const stateDropdownOptions = useMemo(() => stateDropdown, [stateDropdown]);


  const defaultData = {
    insertDate: new Date(),
    // recordStatus: '1', // optionally default this too
    entity: 'PTBGS'
  };

  // useEffect(() => {
  //   const fetchClientState = async () => {
  //     try {
  //       const clientStateItem = await InstrumentService.getClientState();
  //       setClientState(clientStateItem);

  //       if (isEditMode) {
  //         setFormData(selectedItem);
  //         // Only try to fetch QR data if editing
  //         if (selectedItem?.instrumentId) {
  //           instrumentService.findOneQrdet(selectedItem.instrumentId).then((data) => {
  //             setQrData(data);
  //           });
  //         }
  //       } else {
  //         setFormData(defaultData);
  //       }

  //       ASTInstrumentService.getDropdown('AST', 'ENTITY').then((data) => {
  //         setEntityDropdown(data);
  //       });

  //       ASTInstrumentService.getDropdown('AST', 'EQUIPMENT_TYPE').then((data) => {
  //         const filteredEquipTypeResponse = data.filter((equipType: { recordStatus: string; }) => equipType.recordStatus !== '0');
  //         setEquipmentTypeDropdown(filteredEquipTypeResponse);
  //       });


  //     } catch (error) {
  //       showToast({
  //         severity: 'error',
  //         summary: 'Error',
  //         detail: error instanceof Error ? error.message : String(error),
  //       });
  //     }
  //   };

  //   fetchClientState();
  // }, []);

  useEffect(() => {
    const fetchClientState = async () => {
      try {
        const clientStateItem = await InstrumentService.getClientState();
        setClientState(clientStateItem);

        if (isEditMode) {
          setFormData(selectedItem);
          console.log("Form Data for Edit Mode:", selectedItem);
          if (selectedItem?.instrumentId) {
            instrumentService.findOneQrdet(selectedItem.instrumentId).then((data) => {
              setQrData(data);
            });
          }
          fetchEquipmentTypes(selectedItem?.entity); // Default to 'PTBGS' if entity is not set
        } else {
          setFormData(defaultData);
          fetchEquipmentTypes('PTBGS');
        }

        // Fetch dropdown data for "Entity"
        ASTInstrumentService.getDropdown('AST', 'ENTITY').then((data) => {
          setEntityDropdown(data);
        });


      } catch (error) {
        showToast({
          severity: 'error',
          summary: 'Error',
          detail: error instanceof Error ? error.message : String(error),
        });
      }
    };

    fetchClientState();
  }, []);

  useEffect(() => {
    if (entity) {
      fetchEquipmentTypes(entity);  // Fetch filtered or all equipment types based on selected entity
    }
  }, [entity]);

  const fetchEquipmentTypes = async (entity: string) => {
    try {
      // const moduleId = 'AST';  // Assuming the moduleId is 'AST'
      // const referenceGroup = 'EQUIPMENT_TYPE';  // Reference group for equipment types

      if (entity === 'PTBGS') {
        // If entity is 'PTBGS', filter equipment types based on the entity
        const equipmentTypes = await ASTInstrumentService.getEquipmentTypesByEntity('AST', 'EQUIPMENT_TYPE', entity);
        setEquipmentTypeDropdown(equipmentTypes);
        const stateTypes = await ASTInstrumentService.getEquipmentTypesByEntity('AST', 'REGION', entity);
        setStateDropdown(stateTypes); // Set the filtered state types for PTBGS
      } else {
        // For other entities, fetch all equipment types without filtering
        const equipmentTypes = await ASTInstrumentService.getDropdown('AST', 'EQUIPMENT_TYPE');
        setEquipmentTypeDropdown(equipmentTypes);  // Set all equipment types
      }
    } catch (error) {
      console.error("Error fetching equipment types:", error);
    }
  };


  const toolbarTemplate = () => {

    const handleBack = () => {
      navigate(`/machine/instrument`);
    }

    // const handleBack = () => {
    //   // Append the current query string (e.g. "?snNo=123")
    //   navigate(`/machine/instrument${location.search}`);
    // };



    async function submit(): Promise<void> {

      let newErrors: { [key: string]: string } = {};
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          newErrors[field] = "This is a required field.";
          showToast(clientState.required);
        }
      });

      setErrors(newErrors);
      if (Object.keys(newErrors).length === 0) {
        const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

        formData.modifiedDate = new Date(currentDate);
        formData.modifiedBy = clientState.fullName;
        formData.facilityCode = clientState.facilityCode;


        if (!isEditMode) {

          formData.insertDate = new Date(currentDate);
          formData.insertBy = clientState.fullName;
          console.log('formData', formData);
          InstrumentService.createInstrument(formData).then(() => {
            showToast(clientState.success);
            navigate(-1);

          }).catch((error: any) => {

            clientState.error.detail = error;
            showToast(clientState.error);
          });
        }
        else {
          InstrumentService.updateInstrument(formData).then(() => {
            navigate(-1);
            showToast(clientState.success);

          }).catch((error: any) => {
            clientState.error.detail = error.response?.data?.message || 'Failed to update record.';
            showToast(clientState.error);
          });
        }
      }
    }


    return (

      <>

        <Button label={t('save')} icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={submit}></Button>
        <Button label={t('back')} icon="pi pi-reply" style={{ marginRight: '.5em' }} onClick={handleBack}></Button>

      </>
    );
  };

  const handleSnNoInputChange = (e: any) => {
    const { id, value } = e.target;

    setFormData({
      ...formData,
      // [id]: value.replace(/\D/g, '')
      [id]: value.toUpperCase()
    });
  };

  const handleInputChange = (e: any) => {
    const { id, value } = e.target;
    // const numericValue = value.replace(/\D/g, ''); // removes all non-digit characters

    setFormData({
      ...formData,
      // [id]: numericValue
      [id]: value
    });
  };


  const handleDropdownChangeCollection = (e: any) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
  };

  const handleSnNoBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const snNo = event.target.value;

    if (snNo) {

    }
  };



  const renderQRCode = (rowData: any) => {
    return (
      <div className="flex justify-center p-4">
        <QRCodeCanvas value={rowData?.qrValue} size={180} />
      </div>
    );
  };

  function handleUnassignQRCode(qrId: any): void {
    showConfirmDialog("Confirmation", "Are you sure you unassigned this?", () => {
      qrData.instrumentId = null;
      qrData.assignBy = null;
      qrData.assignDate = null;
      InstrumentService.unassignQr(qrData).then(() => {
        showToast(clientState.success);
        navigate(-1);
      }).catch((error) => {
        clientState.error.detail = error.response?.data?.message || 'Failed to create record.';
        showToast(clientState.error);
      });
    });
  }

  return (
    <div className="grid">
      <div className='col-12'>
        <div className='card-title'>
          <h5>Create Instrument Form</h5>
          <BreadcrumbComponent />
        </div>
        <div className='form-title'>
          <Fieldset legend="Create Instrument">
            <div className="field col-12 flex justify-content-center">
              {qrData?.qrValue ? renderQRCode(qrData) : null}
            </div>
            {/* {qrData?.qrId && (
              <>
                <div className="field col-12 flex justify-content-center">
                  <Button
                    label="Unassign QR Code"
                    // icon="pi pi-reply"
                    className="p-button-sm" 
                    style={{ minWidth: 100, fontSize: 16 }} 
                    onClick={() => handleUnassignQRCode(selectedItem?.instrumentId)}
                  />
                </div>
              </>
            )} */}
            <div className="p-fluid form-grid-edit grid">
              <div className="f-field">
                {/* <label htmlFor="entity">Entity</label> */}
                <RequiredLabel text="Entity" required />
                <div className="f-input">
                  <Dropdown
                    id="entity"
                    value={formData?.entity || entity}
                    onChange={(e) => {
                      setEntity(e.value);  // Set selected entity
                      setFormData({ ...formData, entity: e.value });
                      fetchEquipmentTypes(e.value);  // Fetch filtered equipment types based on selected entity
                    }}
                    options={entityDropdownOptions}
                    optionLabel="fullDesc"
                    placeholder="Select One"
                    optionValue="referenceCode"
                    disabled={isEditMode || !isEditMode}
                  />
                  {/* <Dropdown
                  id="entity"
                  value={formData?.entity || entity}
                  onChange={handleDropdownChangeCollection}
                  options={entityDropdownOptions}
                  optionLabel="fullDesc"
                  placeholder="Select One"
                  optionValue="referenceCode"
                  disabled={isEditMode || !isEditMode}
                /> */}

                  {errors.entity && <small className="p-error">{errors.entity}</small>}
                </div>
              </div>

              <div className="f-field">
                <RequiredLabel text="Serial no" required />
                <div className="f-input">
                  <InputText
                    id="snNo"
                    // value={formData?.snNo || ''}
                    value={(formData?.snNo || '').toUpperCase()}
                    onChange={!isEditMode ? handleSnNoInputChange : undefined}
                    onBlur={!isEditMode ? handleSnNoBlur : undefined}
                    readOnly={!!isEditMode}
                  />
                  {errors.snNo && <small className="p-error">{errors.snNo}</small>}
                </div>
              </div>

              <div className="f-field">
                <RequiredLabel text="Item Code" required />
                <div className="f-input">
                  <InputText
                    id="itemCode"
                    value={formData?.itemCode || ''}
                    onChange={!isEditMode ? handleInputChange : undefined}
                    readOnly={!!isEditMode}
                  />
                  {errors.itemCode && <small className="p-error">{errors.itemCode}</small>}
                </div>
              </div>


              <div className="f-field">
                <label className='f-label'>Item Description</label>
                <div className="f-input">
                  <InputText
                    id="itemDesc"
                    value={formData?.itemDesc || ''}
                    onChange={handleInputChange}
                  // inputMode="numeric"
                  // pattern="[0-9]*"
                  />
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Manufacturer Name</label>
                <div className="f-input">
                  <InputText
                    id="manufacturerName"
                    value={formData?.manufacturerName || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="f-field">
                <label className='f-label'>Installation Location</label>
                <div className="f-input">
                  <InputText
                    id="installationLocation"
                    value={formData?.installationLocation || ''}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="f-field">
                {/* <RequiredLabel text="last PM Date" required /> */}
                <label className='f-label'>Last PM Date</label>
                <div className="f-input">
                  <Calendar
                    inputId="lastPmDate"
                    value={formData?.lastPmDate ? new Date(formData.lastPmDate) : null}
                    onChange={(e) => setFormData({ ...formData, lastPmDate: e.value })}
                    dateFormat="dd M yy"
                    icon={<i className="pi pi-calendar" style={{ color: '#fff', backgroundColor: '#FF6347', padding: '5px', borderRadius: '50%' }} />}
                    showIcon
                  >
                    {errors.lastPmDate && <small className="p-error">{errors.lastPmDate}</small>}
                  </Calendar>
                </div>
              </div>

              <div className="f-field">
                {/* <RequiredLabel text="Next PM Date" required /> */}
                <label className='f-label'>Next PM Date</label>
                <div className="f-input">
                  <Calendar
                    inputId="nextPmDate"
                    value={formData?.nextPmDate ? new Date(formData.nextPmDate) : null}
                    onChange={(e) => setFormData({ ...formData, nextPmDate: e.value })}
                    dateFormat="dd M yy"
                    icon={<i className="pi pi-calendar" style={{ color: '#fff', backgroundColor: '#FF6347', padding: '5px', borderRadius: '50%' }} />}
                    showIcon
                  >
                    {errors.nextPmDate && <small className="p-error">{errors.nextPmDate}</small>}
                  </Calendar>
                </div>
              </div>

              <div className="f-field">
                {/* <RequiredLabel text="Installation Date" required /> */}
                <label className='f-label'>Installation Date</label>
                <div className="f-input">
                  <Calendar
                    inputId="installDate"
                    value={formData?.installDate ? new Date(formData.installDate) : null}
                    onChange={(e) => setFormData({ ...formData, installDate: e.value })}
                    dateFormat="dd M yy"
                    icon={<i className="pi pi-calendar" style={{ color: '#fff', backgroundColor: '#FF6347', padding: '5px', borderRadius: '50%' }} />}
                    showIcon
                  >
                    {errors.installDate && <small className="p-error">{errors.installDate}</small>}
                  </Calendar>
                </div>
              </div>

              <div className="f-field">
                {/* <RequiredLabel text="Warranty End Date" required /> */}
                <label className='f-label'>Warranty End Date</label>
                <div className="f-input">
                  <Calendar
                    inputId="warrantyEndDate"
                    value={formData?.warrantyEndDate ? new Date(formData.warrantyEndDate) : null}
                    onChange={(e) => setFormData({ ...formData, warrantyEndDate: e.value })}
                    dateFormat="dd M yy"
                    icon={<i className="pi pi-calendar" style={{ color: '#fff', backgroundColor: '#FF6347', padding: '5px', borderRadius: '50%' }} />}
                    showIcon
                  >
                    {errors.warrantyEndDate && <small className="p-error">{errors.warrantyEndDate}</small>}
                  </Calendar>
                </div>
              </div>

              <div className="f-field">
                <label className='f-label'>Equipment Type</label>
                <div className="f-input">
                  <Dropdown
                    id="equipType"
                    value={formData?.equipType || ''}
                    onChange={handleDropdownChangeCollection}
                    options={equipmentTypeDropdown}  // Use the filtered equipment types
                    optionLabel="fullDesc"
                    placeholder="Select Equipment Type"
                    optionValue="referenceCode"
                    filter
                    showClear
                  />

                  {/* <Dropdown
                  id="equipType"
                  value={formData?.equipType || ''}
                  onChange={handleDropdownChangeCollection}
                  options={equipmentTypeDropdownOptions}
                  optionLabel="fullDesc"
                  placeholder="Select One"
                  optionValue="referenceCode"
                  filter
                  showClear
                /> */}
                </div>
              </div>

              <div className="f-field">
                <label className='f-label'>Region</label>
                <div className="f-input">
                  <Dropdown
                    id="state"
                    value={formData?.state || ''}
                    onChange={handleDropdownChangeCollection}
                    options={stateDropdown}  // Use the filtered state 
                    optionLabel="fullDesc"
                    placeholder="Select state"
                    optionValue="referenceCode"
                    filter
                    showClear
                  />
                </div>
              </div>
              <div className="f-field">
                <label className='f-label'>Status</label>
                <div className="f-input">
                  <InputSwitch
                    checked={formData?.status === 'Active'}
                    onChange={(e) => setFormData({ ...formData, status: e.value ? "Active" : "Inactive" })}
                  />
                </div>
              </div>
            </div>
          </Fieldset>
          <div>
            <br />
          </div>

          <div>
            <br />
          </div>
          <Toolbar end={toolbarTemplate}></Toolbar>
        </div>
      </div>
    </div>
  );
};

export default InstrumentEdit;
